// projects.js (fixed: consistent cell usage, imports, no hook-order issues)
import React, { useMemo, useRef, useState } from 'react';
import { COLUMN_OPTIONS } from './constants';
import { SFDC_STAGE_OPTIONS } from '../common/constants';
import _ from 'lodash';
import salesforce_logo from "./salesforce_logo.png";

import styled from 'styled-components';
import dayjs from 'dayjs';
import currency from 'currency.js';

import Table from '../table';
import {
  Paper, TextField, FormControl, Tooltip, Button, Avatar,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';

// ✅ Needed for DateCell
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';

import BouncingPreloader from '../common/bouncyLoader';
import { SFDC_DOMAIN } from '../../../utils/config/config';

// ---------- styled container ----------
const THEME = {
  colors: { primary: '#093d61', white: '#ffffff', black: '#000000', border: '#d1d5da', borderDark: '#000000' },
  spacing: { xs: '0.25rem', sm: '0.5rem', md: '1rem', lg: '1.5rem', xl: '2rem' },
  typography: { fontFamily: 'Roboto' },
  layout: { headerHeight: '2.5rem', resizerWidth: '7px' },
  breakpoints: { sm: '576px', md: '768px', lg: '992px', xl: '1200px' },
};

const StyledProjectsContainer = styled('div').withConfig({
  shouldForwardProp: (prop) => prop !== 'showDetailedView',
})(({ showDetailedView }) => ({
  '& .projects-paper': {
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    minWidth: 0,
    overflow: 'hidden',
    [`@media (max-width: ${THEME.breakpoints.lg})`]: {
      minHeight: '500px',
    },
    [`@media (max-width: ${THEME.breakpoints.md})`]: {
      minHeight: '400px',
    },
  },
  '& .projects-topbar': {
    padding: THEME.spacing.md, paddingBottom: 0,
    [`@media (max-width: ${THEME.breakpoints.md})`]: { padding: THEME.spacing.sm, paddingBottom: 0 },
  },
  '& .projects-searchbar': {
    display: 'flex', alignItems: 'center', gap: THEME.spacing.md, flexWrap: 'wrap',
    [`@media (max-width: ${THEME.breakpoints.lg})`]: { flexDirection: 'column', alignItems: 'stretch', gap: THEME.spacing.sm },
  },
  '& .projects-search-control': {
    minWidth: '200px', maxWidth: '400px', flex: '1 1 auto',
    [`@media (max-width: ${THEME.breakpoints.md})`]: { minWidth: '150px', maxWidth: '100%' },
  },
  '& .projects-search-buttons': {
    display: 'flex', gap: THEME.spacing.sm, alignItems: 'center',
    [`@media (max-width: ${THEME.breakpoints.lg})`]: { justifyContent: 'center' },
  },
  '& .projects-filter-summary': {
    maxWidth: '60vw', flex: '2 1 auto', fontSize: '0.875rem',
    [`@media (max-width: ${THEME.breakpoints.lg})`]: { maxWidth: '100%', fontSize: '0.8rem' },
    '& b': { fontWeight: 'bold' },
  },
  '& .projects-pmtbd-filter': {
    alignSelf: 'flex-start', color: THEME.colors.primary,
    [`@media (max-width: ${THEME.breakpoints.lg})`]: { alignSelf: 'stretch' },
    '& .MuiButton-root': {
      [`@media (max-width: ${THEME.breakpoints.md})`]: { fontSize: '0.75rem', padding: '6px 12px' },
    },
  },
  '& .projects-table-container': {
    padding: THEME.spacing.md, overflowX: 'auto', flex: 1, width: '100%', maxWidth: '100%',
    [`@media (max-width: ${THEME.breakpoints.md})`]: { padding: THEME.spacing.sm },
    '& .table': {
      display: 'flow-root', borderSpacing: 0, border: `1px solid ${THEME.colors.borderDark}`, borderLeft: 0,
      fontFamily: THEME.typography.fontFamily, overflowY: 'auto', minWidth: '100%',
      '& .tr:last-child .td': { borderBottom: `1px solid ${THEME.colors.borderDark}` },
      '& .th': {
        textAlign: 'left', background: THEME.colors.primary, color: THEME.colors.white,
        height: THEME.layout.headerHeight, padding: THEME.spacing.sm,
        borderBottom: `1px solid ${THEME.colors.borderDark}`, borderRight: `1px solid ${THEME.colors.borderDark}`,
        fontSize: '0.875rem', fontWeight: 'bold',
        [`@media (max-width: ${THEME.breakpoints.md})`]: { fontSize: '0.75rem', padding: '0.25rem', height: '2rem' },
      },
      '& .td': {
        position: 'relative', margin: 0, padding: '0.4rem 0.5rem',
        borderLeft: `1px solid ${THEME.colors.borderDark}`, borderBottom: `1px solid ${THEME.colors.border}`,
        whiteSpace: 'nowrap', overflow: 'hidden', fontSize: '0.875rem',
        [`@media (max-width: ${THEME.breakpoints.md})`]: { padding: '0.25rem', fontSize: '0.75rem' },
        '& .resizer': {
          display: 'inline-block', width: THEME.layout.resizerWidth, height: '100%',
          position: 'absolute', right: 0, top: 0, transform: 'translateX(50%)', zIndex: 1, touchAction: 'none',
        },
      },
    },
  },
  '& .projects-loading': { position: 'absolute', top: '55%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 1000 },
  '& .project-cell': { cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: THEME.spacing.xs, maxWidth: '100%' },
  '& .project-name': {
    whiteSpace: 'pre-wrap', padding: THEME.spacing.xs, borderRadius: '4px', transition: 'all 0.2s ease',
    flex: 1, minWidth: 0, '&:hover': { opacity: 0.8 },
  },
  '& .project-name--selected': { backgroundColor: THEME.colors.primary, color: THEME.colors.white },
  '& .project-name--default': { backgroundColor: THEME.colors.white, color: THEME.colors.black },
  '& .salesforce-button': {
    padding: 0, minWidth: '40px', flexShrink: 0,
    [`@media (max-width: ${THEME.breakpoints.md})`]: { minWidth: '32px' },
    '& .salesforce-avatar': {
      height: '25px', width: '25px',
      [`@media (max-width: ${THEME.breakpoints.md})`]: { height: '20px', width: '20px' },
    },
  },
  '& .pm-assignment': {
    cursor: 'pointer', color: THEME.colors.primary, transition: 'all 0.2s ease',
    padding: THEME.spacing.xs, borderRadius: '4px',
    '&:hover': { backgroundColor: `${THEME.colors.primary}10`, textDecoration: 'underline' },
  },
  '& .date-picker-container .MuiTextField-root': {
    maxWidth: '7rem',
    '& input': { fontSize: '0.875rem', [`@media (max-width: ${THEME.breakpoints.md})`]: { fontSize: '0.75rem' } },
  },
  '& .MuiTextField-root input': {
    fontSize: '0.875rem', [`@media (max-width: ${THEME.breakpoints.md})`]: { fontSize: '0.75rem' },
  },
  '& .MuiAutocomplete-root .MuiInputBase-input': {
    fontSize: '0.875rem', [`@media (max-width: ${THEME.breakpoints.md})`]: { fontSize: '0.75rem' },
  },
}));

// ---------- lightweight editors (isolated components) ----------
const AmountCell = ({ row, value, onUpdate }) => {
  const [text, setText] = React.useState(currency(value, { precision: 0 }).format());
  const handleBlur = (e) => {
    onUpdate(row.original.id, { sfdc_amount: Number(e.target.value.replace(/[^0-9.-]+/g, "")) });
  };
  return (
    <TextField
      id={`amount-field-${row.original.id}`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      size="small"
    />
  );
};

const CostStatusProfitCell = ({ row, value, onUpdate }) => {
  const [text, setText] = React.useState(currency(value, { precision: 0 }).format());
  const handleBlur = (e) => {
    onUpdate(row.original.id, { sfdc_cost_status_profit: Number(e.target.value.replace(/[^0-9.-]+/g, "")) });
  };
  return (
    <TextField
      id={`cost-status-profit-${row.original.id}`}
      value={text}
      onChange={(e) => setText(e.target.value)}
      onBlur={handleBlur}
      size="small"
    />
  );
};

const DateCell = ({ row, value, field, onUpdate, idPrefix }) => {
  const init = value ? dayjs(value).add(21, 'hour') : null;
  const [dt, setDt] = React.useState(init);
  const handleChange = (newVal) => {
    setDt(newVal);
    onUpdate(row.original.id, { [field]: newVal ? newVal.format('YYYY-MM-DD') : null });
  };
  return (
    <div className="date-picker-container">
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <DatePicker
          id={`${idPrefix}-${row.original.id}`}
          value={dt}
          onChange={handleChange}
          format="MMM-DD-YYYY"
          slotProps={{ textField: { size: 'small', sx: { maxWidth: '7rem' } } }}
        />
      </LocalizationProvider>
    </div>
  );
};

// ---------- component ----------
const Projects = (props) => {
  const {
    // Data props
    projectViewData,
    pmResourceNames,
    filters,

    // State props
    loading,
    showColumns,
    showDetailedView,
    detailViewProjectInfo,
    searchQuery,

    // Function props
    updateJobData,
    updateDetailView,
    toggleDetailView,
    onSearchChange,
  } = props;

  const tableInstance = useRef(null);
  const rem_value = useMemo(() => Math.round(window.innerWidth) / 120, []);

  // display: narrow columns in detail view
  const visibleColumns = useMemo(
    () => (showDetailedView ? ['sfdc_opportunity_name'] : showColumns),
    [showDetailedView, showColumns]
  );
  const hiddenColumnsArray = useMemo(() => {
    const baseHidden = COLUMN_OPTIONS
      .filter(col => !visibleColumns.includes(col.column_id))
      .map(a => a.column_id);
    const alwaysHidden = ['assigned_pm_hidden', 'id', 'sfdc_job_number'];
    const dvExtra = showDetailedView ? ['sfdc_groundbreaking','sfdc_project_completion_date','pm_leader','additional_pms'] : [];
    return [...baseHidden, ...alwaysHidden, ...dvExtra];
  }, [visibleColumns, showDetailedView]);

  // columns (memoized)
  const columns = useMemo(() => [
    { header: 'id', accessorKey: 'id', size: 10 * rem_value },
    { header: 'Job Number', accessorKey: 'sfdc_job_number', size: 10 * rem_value },
    {
      header: 'Project',
      accessorKey: 'sfdc_opportunity_name',
      size: 20 * rem_value,
      cell: (cell) => {
        const isSelected = detailViewProjectInfo?.id === cell.row.original.id;
        return (
          <div className="project-cell">
            <div
              id="Opportunity_Detail_View"
              className={`project-name ${isSelected ? 'project-name--selected' : 'project-name--default'}`}
              onClick={() => {
                updateDetailView(cell.row.original);
                if (!showDetailedView) toggleDetailView(true);
              }}
            >
              {cell.getValue()}
            </div>
            <Tooltip title="Open Opportunity in Salesforce">
              <Button
                className="salesforce-button"
                aria-label="salesforce"
                startIcon={<Avatar className="salesforce-avatar" variant="square" src={salesforce_logo} />}
                onClick={() => {
                  window.open(`https://${SFDC_DOMAIN}/lightning/r/Opportunity/${cell.row.original.sfdc_id}/view`, "_blank");
                }}
              />
            </Tooltip>
          </div>
        );
      },
    },
    { header: 'Account', accessorKey: 'sfdc_account_name', size: 12 * rem_value },
    {
      header: 'Amount',
      accessorKey: 'sfdc_amount',
      size: 8 * rem_value,
      cell: (cell) => (
        <AmountCell row={cell.row} value={cell.getValue()} onUpdate={updateJobData} />
      ),
    },
    {
      header: 'Cost Status Profit',
      accessorFn: row => row.sfdc_cost_status_profit,
      id: 'cost_status_profit',
      size: 8 * rem_value,
      cell: (cell) => (
        <CostStatusProfitCell row={cell.row} value={cell.getValue()} onUpdate={updateJobData} />
      ),
    },
    { header: 'Ops Leader', accessorKey: 'sfdc_ops_leader', size: 8 * rem_value, filterFn: "includesSome", enableHiding: true },
    {
      header: 'Probability',
      accessorFn: row => row.sfdc_probability,
      id: 'sfdc_probability',
      size: 5 * rem_value,
      cell: (cell) => `${cell.getValue()}%`,
    },
    {
      header: 'SFDC Stage',
      accessorKey: 'sfdc_stage',
      size: 15 * rem_value,
      sortingFn: (rowA, rowB) => {
        const a = SFDC_STAGE_OPTIONS.find(e => e.title === rowA.original.sfdc_stage);
        const b = SFDC_STAGE_OPTIONS.find(e => e.title === rowB.original.sfdc_stage);
        if (!a || !b) return 0;
        return a.sorting_order - b.sorting_order;
      },
      cell: (cell) => {
        const currentValue = cell.getValue() || null;
        return (
          <Autocomplete
            disablePortal
            disableClearable
            options={SFDC_STAGE_OPTIONS}
            getOptionLabel={(option) => option.title}
            value={SFDC_STAGE_OPTIONS.find(o => o.title === currentValue) || null}
            isOptionEqualToValue={(o, v) => o.title === v.title}
            size="small"
            onChange={(e, value) => {
              if (value) {
                updateJobData(cell.row.original.id, {
                  sfdc_stage: value.title,
                  sfdc_probability: value.probability
                });
              }
            }}
            renderInput={(params) => <TextField {...params} size="small" />}
          />
        );
      }
    },
    {
      header: 'Groundbreak Date',
      accessorKey: 'sfdc_groundbreaking',
      size: 10 * rem_value,
      cell: (cell) => (
        <DateCell
          row={cell.row}
          value={cell.getValue()}
          field="sfdc_groundbreaking"
          onUpdate={updateJobData}
          idPrefix="groundbreak-date"
        />
      )
    },
    {
      header: 'Completion Date',
      accessorKey: 'sfdc_project_completion_date',
      size: 10 * rem_value,
      cell: (cell) => (
        <DateCell
          row={cell.row}
          value={cell.getValue()}
          field="sfdc_project_completion_date"
          onUpdate={updateJobData}
          idPrefix="completion-date"
        />
      )
    },
    { header: 'Division', accessorKey: 'sfdc_division', size: 10 * rem_value },
    { header: 'Company', accessorKey: 'sfdc_company', size: 10 * rem_value },
    {
      header: 'PM Leader',
      accessorFn: row => row.requirements_jobs,
      id: 'pm_leader',
      size: 12 * rem_value,
      cell: (cell) => {
        const requirements_jobs = cell.getValue();
        let pm_leader = 'PM TBD';
        requirements_jobs.forEach((e) => { if (e.requirement_type === 'pm_leader') pm_leader = e.name; });
        return (
          <Tooltip title="Edit PM Assignment">
            <div
              className="pm-assignment"
              onClick={() => {
                updateDetailView(cell.row.original);
                if (!showDetailedView) toggleDetailView(true);
              }}
            >
              {pm_leader}
            </div>
          </Tooltip>
        );
      }
    },
    {
      header: 'Additional PMs',
      accessorFn: row => row.requirements_jobs,
      id: 'additional_pms',
      size: 12 * rem_value,
      cell: (cell) => {
        const requirements_jobs = cell.getValue();
        let additional_pms = null;
        requirements_jobs.forEach((e) => {
          if (e.requirement_type === 'additional_pm') {
            additional_pms = additional_pms ? `${additional_pms}; ${e.name}` : e.name;
          }
        });
        return (
          <Tooltip title="Edit Additional PM Assignment">
            <div
              className="pm-assignment"
              onClick={() => {
                updateDetailView(cell.row.original);
                if (!showDetailedView) toggleDetailView(true);
              }}
            >
              {additional_pms}
            </div>
          </Tooltip>
        );
      }
    },
    {
      header: 'Assigned PM_Hidden',
      accessorFn: row => row.requirements_jobs,
      id: 'assigned_pm_hidden',
      size: 8 * rem_value,
      cell: (cell) => cell.getValue().map(a => a.name).toString(),
    }
  ], [detailViewProjectInfo?.id, showDetailedView, rem_value, updateDetailView, toggleDetailView, updateJobData]);

  const [pmtbdFilter, setPMTBDFilter] = useState(false);
  const [searchInput, setSearchInput] = useState(searchQuery || '');
  
  const filters_to_display = useMemo(() => {
    const f = { ...filters };
    delete f.multiply_by_sfdc_probability;
    return f;
  }, [filters]);

  // Update local search input when searchQuery prop changes
  React.useEffect(() => {
    setSearchInput(searchQuery || '');
  }, [searchQuery]);

  // Handle search submission
  const handleSearchSubmit = () => {
    if (onSearchChange) {
      onSearchChange(searchInput);
    }
  };

  // Handle Enter key press
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSearchSubmit();
    }
  };

  return (
    <StyledProjectsContainer showDetailedView={showDetailedView}>
      {loading ? (
        <div className="projects-loading">
          <BouncingPreloader />
        </div>
      ) : (
        <Paper elevation={7} className="projects-paper" id="project_view">
          {/* Search bar */}
          <div className="projects-topbar">
            <div className="projects-searchbar">
              <FormControl className="projects-search-control">
                <TextField
                  value={searchInput}
                  onChange={e => setSearchInput(e.target.value)}
                  onKeyPress={handleSearchKeyPress}
                  placeholder="Search all projects... (Press Enter)"
                />
              </FormControl>
              <div className="projects-search-buttons">
                <Button
                  variant="contained"
                  onClick={handleSearchSubmit}
                  disabled={loading}
                  size="medium"
                >
                  Search
                </Button>
                {searchQuery && (
                  <Button
                    variant="outlined"
                    onClick={() => {
                      setSearchInput('');
                      if (onSearchChange) {
                        onSearchChange('');
                      }
                    }}
                    disabled={loading}
                    size="medium"
                  >
                    Clear
                  </Button>
                )}
              </div>
              {!showDetailedView && (
                <div className="projects-filter-summary">
                  <div>
                    <b>Applied Filters:</b>&nbsp;
                    {JSON.stringify(_.pickBy(filters_to_display, (value) => value.length !== 0))
                      .slice(1, -1)
                      .replace(/"/g, '')
                      .replace(/,/g, ', ')
                      .replace('divisions:', '  Divisions: ')
                      .replace('ops_leaders:', '  Ops Leaders: ')
                      .replace('companies:', '  Companies: ')
                      .replace('stages:', '  SFDC Stages: ')
                      .replace('probabilities', '  Probabilities: ')
                    }
                  </div>
                </div>
              )}
            </div>
            <div className="projects-pmtbd-filter">
              {!showDetailedView && (
                <Button
                  variant="outlined"
                  onClick={() => {
                    if (pmtbdFilter) {
                      tableInstance.current?.setGlobalFilter(null);
                      setPMTBDFilter(false);
                    } else {
                      tableInstance.current?.setGlobalFilter('PM TBD');
                      setPMTBDFilter(true);
                    }
                  }}
                >
                  {pmtbdFilter ? 'Remove Filter for PM TBD' : 'Filter for PM TBD'}
                </Button>
              )}
            </div>
          </div>

          <div className="projects-table-container">
            <Table
              columns={columns}
              data={projectViewData}
              ref={tableInstance}
              hiddenColumns={hiddenColumnsArray}
            />
          </div>
        </Paper>
      )}
    </StyledProjectsContainer>
  );
};

export default Projects;
